import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import {
  TrendingUp, TrendingDown, Minus, Newspaper, Activity, Globe,
  Clock, Zap, RefreshCw, AlertCircle, Radio,
} from "lucide-react";
import FreshnessDot from "./FreshnessDot";

/**
 * "Next Day Sentiment Prediction" — forward-looking signal aggregator.
 * Reads index futures, after-hours/pre-market, news sentiment, volume spikes,
 * and VIX regime to project tomorrow's direction.
 */
export default function NextDaySentimentPrediction({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [ts, setTs] = useState(null);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    let intId;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/stock/sentiment-prediction/${ticker}`, { timeout: 30000 });
        if (cancelled) return;
        setData(res.data);
        setTs(Date.now());
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Failed to compute sentiment");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    load();
    intId = setInterval(load, 60000); // refresh every 60s
    return () => { cancelled = true; clearInterval(intId); };
  }, [ticker, refreshTick]);

  if (loading && !data) {
    return (
      <div className="rounded border border-gray-800 bg-gray-900/60 p-6 text-center">
        <Radio className="w-8 h-8 text-cyan-400 mx-auto animate-pulse" />
        <div className="mt-2 text-sm text-gray-300">Reading information flow for {ticker}…</div>
      </div>
    );
  }
  if (error) return (
    <div className="p-3 space-y-2" data-testid="sentiment-prediction-skeleton">
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-block w-2 h-2 rounded-full bg-amber-400/60 animate-pulse" />
        <span className="text-[11px] text-amber-300/80">Refreshing sentiment prediction…</span>
      </div>
      <div className="h-32 bg-slate-800/40 rounded animate-pulse" />
    </div>
  );
  if (!data) return null;

  const isUp = data.prediction === "UP";
  const isDown = data.prediction === "DOWN";
  const sigs = data.signals_used || {};
  const futures = sigs.index_futures || {};
  const eh = sigs.extended_hours || {};
  const news = sigs.news_sentiment || {};
  const vol = sigs.volume_volatility || {};
  const vix = sigs.vix_regime || {};

  return (
    <div className="space-y-3" data-testid="next-day-sentiment-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 bg-[#0d0d0d] border border-gray-800 rounded p-3">
        <div className="flex items-center gap-2">
          <Radio className="w-5 h-5 text-cyan-400" />
          <h3 className="text-base font-semibold text-white">{ticker} · Next Day Sentiment</h3>
          {ts && <FreshnessDot timestamp={ts} />}
          <span className="text-[10px] text-gray-500 font-mono">refreshes every 60s</span>
        </div>
        <button
          onClick={() => setRefreshTick((v) => v + 1)}
          data-testid="ndsp-refresh"
          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-700 bg-gray-900 hover:bg-gray-800"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Prediction card */}
      <div
        data-testid="ndsp-prediction-card"
        className={`rounded-lg border-2 p-5 text-center ${
          isUp ? "border-green-600 bg-gradient-to-br from-green-950/50 to-emerald-950/30"
          : isDown ? "border-red-600 bg-gradient-to-br from-red-950/50 to-rose-950/30"
          : "border-gray-700 bg-gray-900/40"
        }`}
      >
        <div className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">
          Predicted opening tone for next session
        </div>
        <div className="mt-3 flex items-center justify-center gap-3">
          {isUp ? <TrendingUp className="w-14 h-14 text-green-400" />
            : isDown ? <TrendingDown className="w-14 h-14 text-red-400" />
            : <Minus className="w-14 h-14 text-gray-500" />}
          <div className={`text-5xl font-extrabold font-mono ${isUp ? "text-green-300" : isDown ? "text-red-300" : "text-gray-300"}`}
               data-testid="ndsp-prediction-direction">
            {data.prediction}
          </div>
        </div>
        <div className="text-2xl font-bold font-mono text-white mt-2" data-testid="ndsp-confidence">
          {data.confidence_pct}% conviction
        </div>
        <div className="text-[11px] text-gray-400 mt-1 font-mono">
          composite score = {data.composite_score >= 0 ? "+" : ""}{data.composite_score} (range −1 to +1)
        </div>
        <div className={`inline-block mt-3 px-3 py-1 rounded text-xs font-semibold border ${
          Math.abs(data.composite_score) >= 0.45 ? "border-cyan-700 bg-cyan-950/40 text-cyan-200"
          : Math.abs(data.composite_score) >= 0.25 ? "border-blue-700 bg-blue-950/40 text-blue-200"
          : "border-gray-700 bg-gray-900/60 text-gray-300"
        }`} data-testid="ndsp-verdict">
          {data.verdict}
        </div>
      </div>

      {/* Per-signal contribution bars */}
      <div className="rounded border border-gray-800 bg-[#0a0a0a] p-3 space-y-2" data-testid="ndsp-contributions">
        <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Signal Contributions</div>
        <div className="space-y-1.5">
          {data.contributions.map((c) => (
            <ContributionBar key={c.key} c={c} />
          ))}
        </div>
        <p className="text-[10px] text-gray-500 leading-snug">
          Each signal returns a score in [-1, +1]. Final composite = Σ(score × weight) ÷ Σ(weights of available signals).
        </p>
      </div>

      {/* Signal detail panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Index futures */}
        <Panel
          icon={<Globe className="w-4 h-4 text-amber-400" />}
          title="Overnight Index Futures"
          subtitle={futures.explanation}
          score={futures.score}
          testid="ndsp-futures-panel"
        >
          {(futures.rows || []).length > 0 ? (
            <table className="w-full text-xs">
              <tbody>
                {futures.rows.map((f) => (
                  <tr key={f.symbol} className="border-b border-gray-800/40">
                    <td className="py-1 px-2 font-mono text-gray-400 text-[11px]">{f.symbol}</td>
                    <td className="py-1 px-2 text-gray-200">{f.name}</td>
                    <td className="py-1 px-2 text-right font-mono">${f.price}</td>
                    <td className={`py-1 px-2 text-right font-mono font-bold ${f.change_pct > 0 ? "text-green-300" : f.change_pct < 0 ? "text-red-300" : "text-gray-400"}`}>
                      {f.change_pct >= 0 ? "+" : ""}{f.change_pct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="text-[11px] text-gray-500">Futures data unavailable.</div>}
        </Panel>

        {/* Extended hours */}
        <Panel
          icon={<Clock className="w-4 h-4 text-indigo-400" />}
          title="After-Hours / Pre-Market"
          subtitle={eh.explanation}
          score={eh.score}
          testid="ndsp-eh-panel"
        >
          {(eh.sessions || []).length > 0 ? (
            <div className="space-y-1.5">
              {eh.sessions.map((s) => (
                <div key={s.session} className="flex items-center justify-between text-xs bg-gray-900/40 rounded px-2 py-1">
                  <span className="font-mono text-gray-400 text-[10px] uppercase">
                    {s.session === "after_hours" ? "After-Hours" : "Pre-Market"}
                  </span>
                  <span className="font-mono">${s.last_print}</span>
                  <span className={`font-mono font-bold ${s.change_pct > 0 ? "text-green-300" : s.change_pct < 0 ? "text-red-300" : "text-gray-400"}`}>
                    {s.change_pct >= 0 ? "+" : ""}{s.change_pct}%
                  </span>
                  <span className="text-gray-500 text-[10px] font-mono">{s.bar_count} bars</span>
                </div>
              ))}
            </div>
          ) : <div className="text-[11px] text-gray-500">No extended-hours activity.</div>}
        </Panel>

        {/* News sentiment */}
        <Panel
          icon={<Newspaper className="w-4 h-4 text-blue-400" />}
          title="News Sentiment (last 36h)"
          subtitle={news.explanation}
          score={news.score}
          testid="ndsp-news-panel"
        >
          {news.counts && (
            <div className="flex items-center gap-2 text-[11px] mb-2">
              <span className="px-1.5 py-0.5 rounded bg-green-950/40 text-green-300 border border-green-800/40 font-mono">+{news.counts.positive}</span>
              <span className="px-1.5 py-0.5 rounded bg-red-950/40 text-red-300 border border-red-800/40 font-mono">−{news.counts.negative}</span>
              <span className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 border border-gray-700 font-mono">~{news.counts.neutral}</span>
              <span className="text-gray-500 font-mono">total {news.counts.total}</span>
            </div>
          )}
          {(news.items || []).length > 0 ? (
            <ul className="space-y-1 max-h-32 overflow-y-auto">
              {news.items.slice(0, 5).map((i, idx) => (
                <li key={idx} className="flex items-start gap-2 text-[11px] border-b border-gray-800/40 pb-1">
                  <span className={`w-1.5 h-1.5 mt-1 rounded-full flex-shrink-0 ${
                    i.sentiment === "positive" ? "bg-green-400"
                    : i.sentiment === "negative" ? "bg-red-400" : "bg-gray-500"
                  }`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-gray-200 truncate" title={i.title}>{i.title}</div>
                    <div className="text-gray-500 text-[9px] font-mono">{i.source} · {i.hours_old}h ago</div>
                  </div>
                </li>
              ))}
            </ul>
          ) : <div className="text-[11px] text-gray-500">No news within the last 36 hours.</div>}
        </Panel>

        {/* Volume + volatility */}
        <Panel
          icon={<Activity className="w-4 h-4 text-fuchsia-400" />}
          title="Volume + Volatility Spike"
          subtitle={vol.explanation}
          score={vol.score}
          testid="ndsp-volume-panel"
        >
          {vol.score !== null && vol.score !== undefined ? (
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <Mini label="Volume vs 20d" value={vol.volume_ratio ? `${vol.volume_ratio}×` : "—"} />
              <Mini label="Range vs ATR" value={vol.range_ratio ? `${vol.range_ratio}×` : "—"} />
              <Mini
                label="Today move"
                value={vol.intraday_pct != null ? `${vol.intraday_pct >= 0 ? "+" : ""}${vol.intraday_pct}%` : "—"}
                color={vol.intraday_pct > 0 ? "text-green-300" : vol.intraday_pct < 0 ? "text-red-300" : "text-gray-300"}
              />
            </div>
          ) : <div className="text-[11px] text-gray-500">No volume data.</div>}
        </Panel>

        {/* VIX regime */}
        <Panel
          icon={<Zap className="w-4 h-4 text-yellow-400" />}
          title="VIX Regime"
          subtitle={vix.explanation}
          score={vix.score}
          testid="ndsp-vix-panel"
        >
          {vix.vix != null ? (
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <Mini label="VIX" value={vix.vix} />
              <Mini
                label="Δ"
                value={`${vix.vix_change_pct >= 0 ? "+" : ""}${vix.vix_change_pct}%`}
                color={vix.vix_change_pct < 0 ? "text-green-300" : vix.vix_change_pct > 0 ? "text-red-300" : "text-gray-300"}
              />
              <Mini label="Regime" value={vix.regime} small />
            </div>
          ) : <div className="text-[11px] text-gray-500">VIX unavailable.</div>}
        </Panel>
      </div>

      {/* Honesty footer */}
      <div className="rounded border border-cyan-900/40 bg-cyan-950/20 p-3 text-[11px] text-cyan-200/80">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
          <div>
            <strong className="text-cyan-200">What this measures:</strong> forward-looking information
            flow that the market hasn't fully priced yet — not yesterday's price patterns. Best used in the
            final hour of regular trading or in the pre-market window. Index futures dominate before 9:30 ET;
            after-hours / pre-market dominate around earnings; news sentiment matters more around major
            announcements; VIX shifts matter most when extreme.
          </div>
        </div>
      </div>
    </div>
  );
}

function ContributionBar({ c }) {
  const pct = c.weight * 100;
  const w = c.weighted ?? 0;
  const widthPct = c.score === null ? 0 : Math.min(100, Math.abs(w) * 200);  // visual: weighted × 2 = bar width
  const positive = w >= 0;
  const dimmed = !c.available;
  return (
    <div className={`flex items-center gap-2 text-xs ${dimmed ? "opacity-40" : ""}`} data-testid={`ndsp-contrib-${c.key}`}>
      <div className="w-44 flex-shrink-0 text-gray-300 font-medium truncate">{c.label}</div>
      <div className="flex-shrink-0 w-12 text-[10px] text-gray-500 font-mono">w {pct.toFixed(0)}%</div>
      <div className="flex-1 relative h-4 bg-gray-900 border border-gray-800 rounded overflow-hidden">
        <div className="absolute inset-y-0 left-1/2 w-px bg-gray-700" />
        {c.score !== null && c.score !== undefined && (
          <div
            className={`absolute inset-y-0 ${positive ? "left-1/2" : "right-1/2"} ${positive ? "bg-green-500/70" : "bg-red-500/70"}`}
            style={{ width: `${widthPct / 2}%` }}
          />
        )}
      </div>
      <div className={`w-16 text-right font-mono text-[11px] ${
        c.score === null || c.score === undefined ? "text-gray-600"
        : w > 0.05 ? "text-green-300"
        : w < -0.05 ? "text-red-300"
        : "text-gray-400"
      }`}>
        {c.score === null || c.score === undefined ? "n/a" : `${w >= 0 ? "+" : ""}${w.toFixed(3)}`}
      </div>
    </div>
  );
}

function Panel({ icon, title, subtitle, score, children, testid }) {
  const scoreColor = score === null || score === undefined ? "text-gray-500"
    : score > 0.2 ? "text-green-300" : score < -0.2 ? "text-red-300" : "text-gray-300";
  return (
    <div className="rounded border border-gray-800 bg-[#0a0a0a] p-3 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {icon}
          <span className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{title}</span>
        </div>
        {score !== null && score !== undefined && (
          <span className={`font-mono font-bold text-sm ${scoreColor}`}>
            {score >= 0 ? "+" : ""}{Number(score).toFixed(2)}
          </span>
        )}
      </div>
      {subtitle && <div className="text-[10px] text-gray-500">{subtitle}</div>}
      {children}
    </div>
  );
}

function Mini({ label, value, color = "text-gray-200", small = false }) {
  return (
    <div className="bg-gray-900/40 border border-gray-800 rounded p-1.5">
      <div className="text-[9px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`font-mono font-semibold ${small ? "text-[10px]" : "text-sm"} ${color}`}>{value}</div>
    </div>
  );
}
